import { useState } from 'react';
import { useScrollReveal } from '../../../hooks/useScrollReveal';

const FAQS = [
  {
    question: '¿Qué es la mediación y en qué se diferencia del juicio?',
    answer:
      'La mediación es un proceso voluntario y confidencial en el que un mediador imparcial ayuda a las partes a dialogar y alcanzar un acuerdo satisfactorio para ambas. A diferencia del juicio, no hay un juez que imponga una resolución: son las propias partes quienes deciden el resultado. Es más rápida, económica y preserva las relaciones personales o comerciales.',
  },
  {
    question: '¿Cuánto tiempo dura un proceso de mediación?',
    answer:
      'Depende de la complejidad del caso, pero en general un proceso de mediación se resuelve entre 2 y 8 semanas. En conflictos sencillos puede bastar con 2 o 3 sesiones de una hora y media. En asuntos más complejos, como separaciones con patrimonio o conflictos societarios, puede requerir entre 4 y 6 sesiones. En cualquier caso, es significativamente más rápido que un procedimiento judicial.',
  },
  {
    question: '¿Los acuerdos alcanzados en mediación son legalmente vinculantes?',
    answer:
      'Sí. Los acuerdos de mediación tienen plena validez legal y pueden ejecutarse judicialmente si alguna de las partes no los cumple. Además, pueden elevarse a escritura pública ante notario, lo que les otorga la misma eficacia que una sentencia judicial. En mediación familiar, el acuerdo debe ser homologado por el Juzgado de Familia para tener eficacia plena.',
  },
  {
    question: '¿Cuánto cuesta la mediación?',
    answer:
      'El coste varía según la naturaleza y duración del caso. La primera consulta es siempre gratuita y sin compromiso. En general, la mediación resulta considerablemente más económica que un procedimiento judicial, ya que evita los costes de abogados, procuradores y tasas judiciales durante años. Consulta nuestras tarifas en la primera sesión orientativa.',
  },
  {
    question: '¿La mediación es completamente confidencial?',
    answer:
      'Absolutamente. La confidencialidad es uno de los principios fundamentales de la mediación, recogido en el artículo 9 de la Ley 5/2012 de Mediación. Todo lo que se comunique durante el proceso no puede utilizarse en un procedimiento judicial posterior. El mediador también está obligado por un deber de confidencialidad incluso frente a los tribunales.',
  },
  {
    question: '¿Qué ocurre si no llegamos a un acuerdo?',
    answer:
      'La mediación es voluntaria y cualquier parte puede abandonarla en cualquier momento sin consecuencias legales. Si no se alcanza acuerdo, las partes conservan todos sus derechos para acudir a la vía judicial o al arbitraje. Lo que se haya hablado durante la mediación no puede utilizarse como prueba en un proceso judicial posterior.',
  },
  {
    question: '¿Se puede hacer la mediación de forma online?',
    answer:
      'Sí. Ofrecemos mediación completamente online mediante videoconferencia segura. Esta modalidad es especialmente útil cuando las partes residen en ciudades diferentes o en el extranjero, o simplemente prefieren no desplazarse. El proceso tiene la misma validez legal y eficacia que la mediación presencial.',
  },
  {
    question: '¿Necesito un abogado para acudir a la mediación?',
    answer:
      'No es obligatorio, aunque puede ser conveniente en asuntos de cierta complejidad jurídica. En mediación familiar, es habitual que cada parte consulte con su abogado antes de firmar el acuerdo final. En cualquier caso, nuestros mediadores están certificados por el Ministerio de Justicia y te orientarán sobre si es recomendable contar con asistencia jurídica en tu caso.',
  },
];

export default function Faq() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  const { ref, isVisible } = useScrollReveal<HTMLElement>();

  const toggle = (i: number) => setOpenIndex(openIndex === i ? null : i);

  return (
    <section ref={ref} id="faq" className="py-28 bg-white">
      <div className="max-w-7xl mx-auto px-6">

        {/* Header */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-14 items-start">

          {/* Left: intro sticky */}
          <div
            className={`lg:col-span-2 lg:sticky lg:top-28 transition-all duration-700 ease-out ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
            style={{ transitionDelay: isVisible ? '100ms' : '0ms' }}
          >
            <span className="text-[#3D9E8C] text-sm font-semibold tracking-widest uppercase">
              Preguntas frecuentes
            </span>
            <h2
              className="mt-3 text-4xl md:text-5xl font-bold text-[#1B3A5C] leading-tight"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Todo lo que<br />necesitas saber
            </h2>
            <p className="mt-5 text-gray-500 text-[15px] leading-relaxed">
              Resolvemos las dudas más habituales sobre el proceso de mediación para que puedas tomar una decisión informada.
            </p>

            {/* Stats */}
            <div className="mt-8 flex flex-col gap-4">
              {[
                { icon: 'ri-time-line', value: '2–8 semanas', label: 'Duración media del proceso' },
                { icon: 'ri-percent-line', value: '95%', label: 'Tasa de éxito en acuerdos' },
                { icon: 'ri-money-euro-circle-line', value: '-70%', label: 'Ahorro frente a la vía judicial' },
              ].map((s, idx) => (
                <div
                  key={s.label}
                  className={`flex items-center gap-4 p-4 bg-[#F4F7FA] rounded-xl transition-all duration-700 ease-out ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                  style={{ transitionDelay: isVisible ? `${250 + idx * 100}ms` : '0ms' }}
                >
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-[#E8F4F1] flex-shrink-0">
                    <i className={`${s.icon} text-[#3D9E8C] text-lg`} />
                  </div>
                  <div>
                    <p className="text-[#1B3A5C] font-bold text-base leading-none">{s.value}</p>
                    <p className="text-gray-400 text-xs mt-1">{s.label}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* CTA */}
            <button
              onClick={() => {
                const el = document.getElementById('contacto');
                if (el) el.scrollIntoView({ behavior: 'smooth' });
              }}
              className="mt-8 inline-flex items-center gap-2 px-6 py-3.5 bg-[#1B3A5C] text-white text-sm font-semibold rounded-full hover:bg-[#14304f] transition-colors duration-200 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-question-answer-line text-base" />
              ¿Tienes más preguntas? Consúltanos
            </button>
          </div>

          {/* Right: accordion */}
          <div className="lg:col-span-3 flex flex-col divide-y divide-gray-100">
            {FAQS.map((faq, i) => {
              const isOpen = openIndex === i;
              return (
                <div
                  key={i}
                  className={`py-5 transition-all duration-700 ease-out ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                  style={{ transitionDelay: isVisible ? `${150 + i * 70}ms` : '0ms' }}
                >
                  <button
                    onClick={() => toggle(i)}
                    className="w-full flex items-start justify-between gap-4 text-left cursor-pointer group"
                  >
                    <span
                      className={`text-[15px] font-semibold leading-snug transition-colors duration-200 ${
                        isOpen ? 'text-[#3D9E8C]' : 'text-[#1B3A5C] group-hover:text-[#3D9E8C]'
                      }`}
                    >
                      {faq.question}
                    </span>
                    <div
                      className={`w-7 h-7 flex items-center justify-center rounded-full flex-shrink-0 mt-0.5 transition-all duration-200 ${
                        isOpen
                          ? 'bg-[#3D9E8C] text-white rotate-45'
                          : 'bg-[#F4F7FA] text-[#1B3A5C] group-hover:bg-[#E8F4F1]'
                      }`}
                    >
                      <i className="ri-add-line text-base" />
                    </div>
                  </button>

                  {/* Answer with smooth animation */}
                  <div
                    className={`overflow-hidden transition-all duration-300 ease-in-out ${
                      isOpen ? 'max-h-96 opacity-100 mt-4' : 'max-h-0 opacity-0'
                    }`}
                  >
                    <p className="text-gray-500 text-sm leading-relaxed pr-10">
                      {faq.answer}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>

        </div>
      </div>
    </section>
  );
}
